import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Link } from '@mui/material';
import { ThirdParty } from '@/components/biz/third-party';
import { FormUsernameInput } from '@/components/form/form-username-input';
import { StepBox } from '@/components/layout/step-box';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useCustom } from '@/hooks/use-custom';
import { useFlow } from '@/hooks/use-flow';
import { useLidForm } from '@/hooks/use-lid-form';
import { AppCoreContext } from '@/providers/core-provider';

type Params = { email: string; areaCode: string; phone: string };

export const StepVerifyUserName = () => {
  const { authType, email, areaCode, phone, setBizState, supportPassKey, socialAuthRedirecting } = useBizState();
  const { setFlow } = useFlow();
  const { t } = useTranslation();
  const { lenovoId } = useContext(AppCoreContext);
  const { control, setError, handleSubmit, setFocus, setValue, clearErrors } = useLidForm<Params>({
    defaultValues: { email, areaCode, phone },
  });

  const { isSingleAuthType } = useCustom();

  const { run, loading } = useCoreRequest(
    (params: Params) => {
      const userName = authType === 'email' ? params.email : params.phone;
      const areaCode = authType === 'phone' ? params.areaCode : '';

      setBizState({
        authType,
        userName,
        areaCode: params.areaCode,
        email: params.email,
        phone: params.phone,
      });

      return lenovoId.loginVerifyUserName({ userName, areaCode, supportPassKey });
    },
    {
      manual: true,
      onSuccess: ({ data, nextSteps }) => {
        setBizState(data);
        if (nextSteps === Flow.LOGIN_STEP_INPUT_PASSWORD) {
          setFlow(Flow.LOGIN_STEP_INPUT_PASSWORD);
          return;
        } else if (nextSteps === Flow.LOGIN_STEP_ROAM_GRANT) {
          if (data.tempCode && data.roam) {
            setFlow(Flow.LOGIN_STEP_ROAM_GRANT);
            return;
          }
        }
        setFlow(Flow.EXCEPTION_SOMETHING_WENT_WRONG);
      },
      onError: (code) => {
        switch (code) {
          case ErrorCode.InvalidUsername:
            setError(authType, authType === 'phone' ? t('phone_format_error') : t('email_format_error'));
            return true;
          case ErrorCode.UsernameNotExists:
            setError(authType, t('account_user_not_found_error'));
            return true;
          case ErrorCode.UserFrozen:
            setFlow(Flow.LOGIN_STEP_DISABLE_RECOVERY);
            return true;
          case ErrorCode.UserDisabled:
            setFlow(Flow.LOGIN_STEP_DISABLE);
            return true;
        }
      },
    },
  );
  const toggleAuthType = () => {
    const type = authType === 'email' ? 'phone' : 'email';
    setValue(authType, '');
    clearErrors(authType);
    setBizState({ authType: type });
    setTimeout(() => setFocus(type));
  };

  const handleCreate = () => setFlow(Flow.REGISTER_STEP_INPUT_USERINFO, { resetBiz: true });

  return (
    <StepBox title={authType === 'email' ? t('email_login_title') : t('phone_login_title')}>
      <form onSubmit={handleSubmit(run)} noValidate>
        <FormUsernameInput control={control} type={authType} />

        {!isSingleAuthType && (
          <Link
            disabled={loading}
            color={loading ? 'textDisabled' : 'primary'}
            component="button"
            type="button"
            sx={{ mt: 2 }}
            variant="body1"
            onClick={toggleAuthType}
          >
            {authType === 'email' ? t('switch_to_phone_button') : t('switch_to_email_button')}
          </Link>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          disabled={socialAuthRedirecting || loading}
          loading={loading}
          loadingPosition="start"
        >
          {t('continue')}
        </Button>
        <div className="mt-2 flex flex-col gap-4">
          <Button
            fullWidth
            variant="text"
            className="mb-4 mt-2"
            disabled={socialAuthRedirecting}
            onClick={handleCreate}
          >
            {t('lenovo_id_creation_button')}
          </Button>

          <ThirdParty source="login" disabled={loading || socialAuthRedirecting} />
        </div>
      </form>
    </StepBox>
  );
};
